/**
 * Provides a centralized administration interface for managing common
 * application configurations. Supports searching, viewing, and editing
 * configuration categories such as session management, accessibility,
 * themes, notifications, language settings, onboarding, URLs, role names,
 * feature names, and feedback forms through dynamically rendered settings panels.
 */
import React, { useMemo, useState } from "react";

import AccessibilityConfig from "./AccessibilityConfig";
import AppTourConfig from "./AppTourConfig";
import AppUrlConfig from "./AppURLConfig";
import FeatureNameConfig from "./FeatureNameConfig";
import FeedbackFormConfig from "./FeedbackFormConfig";
import LanguageSwitchConfig from "./LanguageSwitchConfig";
import NotificationConfig from "./NotificationConfig";
import RoleNameConfig from "./RoleNameConfig";
import SessionConfig from "./SessionConfig";
import ThemesConfig from "./ThemesConfig";

interface CommonConfigurationItem {
  itemDisplayName: string;
  itemComponentName: string;
  isEditable: boolean;
  displayOrder: number;
}

interface Props {
  items: CommonConfigurationItem[];
}

const componentMap: Record<string, React.ComponentType<any>> = {
  SessionConfig,
  AppTourConfig,
  NotificationConfig,
  LanguageSwitchConfig,
  ThemesConfig,
  AccessibilityConfig,
  AppUrlConfig,
  RoleNameConfig,
  FeatureNameConfig,
  FeedbackFormConfig,
};

const CommonConfiguration: React.FC<Props> = ({ items }) => {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return items.filter((item) =>
      item.itemDisplayName
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [items, search]);
  

  return (
    <div className="bg-[#f6f4ef] min-h-screen font-sans">
      <div className="p-5">
        <div className="bg-white border border-gray-200 rounded-md">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center">
            <div className="flex-1">
              <h2 className="text-md font-semibold text-gray-800">
                Common Configuration
              </h2>
            </div>
          </div>

          <div className="p-5">
            <input
              type="text"
              placeholder="Search..."
              className="w-full border border-gray-200 p-2 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-orange-400"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className="space-y-3">
              {filtered.map((item, index) => {
                const isExpanded =
                  expanded === item.itemDisplayName;

                const Component =
                  componentMap[item.itemComponentName];

                return (
                  <div
                    key={`${item.itemComponentName}-${index}`}
                    className="border border-gray-200 rounded-md bg-white"
                  >
                    <div className="p-4 flex justify-between items-center">
                      <div className="text-sm font-medium text-gray-800">
                        {item.itemDisplayName}
                      </div>

                      <button
                        className={`px-3 py-1.5 rounded-md text-sm ${
                          item.isEditable
                            ? "bg-[var(--color-brand)] text-white"
                            : "bg-gray-200 text-gray-500 cursor-not-allowed"
                        }`}
                        disabled={!item.isEditable}
                        onClick={() =>
                          item.isEditable &&
                          setExpanded(
                            isExpanded
                              ? null
                              : item.itemDisplayName
                          )
                        }
                      >
                        {isExpanded ? "Close" : "Edit"}
                      </button>
                    </div>

                    {isExpanded && Component && (
                      <div className="border-t border-gray-200 p-4 bg-[#fbfaf7]">
                        <Component />
                      </div>
                    )}
                  </div>
                );
              })}

              {filtered.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-6">
                  No items found
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonConfiguration;